import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getLeases, createLease, updateLease, deleteLease, getProperties, getUsers } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Edit, Trash2, Building2, User, Calendar, DollarSign, Upload, X, Download } from 'lucide-react';

export default function LeasesPage() {
    const { user } = useAuth();
    const [leases, setLeases] = useState([]);
    const [properties, setProperties] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLease, setEditingLease] = useState(null);
    const [leaseDocument, setLeaseDocument] = useState(null);
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        property_id: '',
        tenant_id: '',
        start_date: '',
        end_date: '',
        monthly_rent: '',
        security_deposit: '',
    });

    const isManager = user?.role === 'property_manager';

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [leasesRes, propertiesRes, tenantsRes] = await Promise.all([
                getLeases(),
                isManager ? getProperties() : Promise.resolve({ data: [] }),
                isManager ? getUsers('tenant') : Promise.resolve({ data: [] }),
            ]);
            setLeases(leasesRes.data);
            setProperties(propertiesRes.data);
            setTenants(tenantsRes.data);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToSend = {
                ...formData,
                monthly_rent: parseFloat(formData.monthly_rent),
                security_deposit: parseFloat(formData.security_deposit),
                lease_document: leaseDocument,
            };

            if (editingLease) {
                await updateLease(editingLease.id, {
                    start_date: formData.start_date,
                    end_date: formData.end_date,
                    monthly_rent: dataToSend.monthly_rent,
                    lease_document: leaseDocument,
                });
                toast.success('Lease updated successfully');
            } else {
                await createLease(dataToSend);
                toast.success('Lease created successfully');
            }
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        }
    };

    const handleDocumentUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('File too large. Max size is 10MB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setLeaseDocument(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleEdit = (lease) => {
        setEditingLease(lease);
        setFormData({
            property_id: lease.property_id,
            tenant_id: lease.tenant_id,
            start_date: lease.start_date,
            end_date: lease.end_date,
            monthly_rent: lease.monthly_rent.toString(),
            security_deposit: lease.security_deposit.toString(),
        });
        setLeaseDocument(lease.lease_document || null);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this lease?')) return;
        try {
            await deleteLease(id);
            toast.success('Lease deleted successfully');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete lease');
        }
    };

    const resetForm = () => {
        setEditingLease(null);
        setFormData({
            property_id: '',
            tenant_id: '',
            start_date: '',
            end_date: '',
            monthly_rent: '',
            security_deposit: '',
        });
        setLeaseDocument(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const getLeaseStatus = (endDate) => {
        const end = new Date(endDate);
        const now = new Date();
        const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        
        if (daysLeft < 0) return { label: 'Expired', class: 'badge-error' };
        if (daysLeft <= 30) return { label: 'Expiring Soon', class: 'badge-warning' };
        return { label: 'Active', class: 'badge-success' };
    };

    const handleDownloadDocument = (lease) => {
        if (!lease.lease_document) {
            toast.error('No document available');
            return;
        }
        
        // Create a download link for the base64 document
        const link = document.createElement('a');
        link.href = lease.lease_document;
        link.download = `lease_${lease.tenant_name?.replace(/\s+/g, '_')}_${lease.property_name?.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Document downloaded!');
    };

    if (loading) {
        return (
            <DashboardLayout title="Leases">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Leases">
            <div className="space-y-6" data-testid="leases-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <p className="text-slate-500">
                            {isManager ? 'Manage all lease agreements' : 'View lease agreements'}
                        </p>
                    </div>
                    {isManager && (
                        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-lease-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Create Lease
                        </Button>
                    )}
                </div>

                {leases.length === 0 ? (
                    <Card className="p-12">
                        <div className="empty-state">
                            <FileText className="empty-state-icon" />
                            <h3 className="empty-state-title">No leases yet</h3>
                            <p className="empty-state-description">
                                {isManager ? 'Create your first lease agreement.' : 'No lease agreements found.'}
                            </p>
                        </div>
                    </Card>
                ) : (
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Tenant</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Monthly Rent</th>
                                        <th>Document</th>
                                        <th>Status</th>
                                        {isManager && <th>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {leases.map((lease) => {
                                        const status = getLeaseStatus(lease.end_date);
                                        return (
                                            <tr key={lease.id} data-testid={`lease-row-${lease.id}`}>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <Building2 className="w-4 h-4 text-slate-400" />
                                                        <div>
                                                            <p className="font-medium text-slate-800">{lease.property_name}</p>
                                                            <p className="text-xs text-slate-500">{lease.property_address}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-slate-400" />
                                                        {lease.tenant_name}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-slate-400" />
                                                        {formatDate(lease.start_date)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-slate-400" />
                                                        {formatDate(lease.end_date)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <DollarSign className="w-4 h-4 text-slate-400" />
                                                        <span className="font-medium">{formatCurrency(lease.monthly_rent)}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {lease.lease_document ? (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleDownloadDocument(lease)}
                                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                            data-testid={`download-doc-${lease.id}`}
                                                        >
                                                            <Download className="w-4 h-4 mr-1" />
                                                            Download
                                                        </Button>
                                                    ) : (
                                                        <span className="text-slate-400 text-sm">No document</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`badge ${status.class}`}>{status.label}</span>
                                                </td>
                                                {isManager && (
                                                    <td>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleEdit(lease)}
                                                                data-testid={`edit-lease-${lease.id}`}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDelete(lease.id)}
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                data-testid={`delete-lease-${lease.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="lease-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingLease ? 'Edit Lease' : 'Create New Lease'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!editingLease && (
                            <>
                                <div className="space-y-2">
                                    <Label>Property</Label>
                                    <Select
                                        value={formData.property_id}
                                        onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                                    >
                                        <SelectTrigger data-testid="lease-property-select">
                                            <SelectValue placeholder="Select property" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {properties.map((property) => (
                                                <SelectItem key={property.id} value={property.id}>
                                                    {property.name} - {property.address}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Tenant</Label>
                                    <Select
                                        value={formData.tenant_id}
                                        onValueChange={(value) => setFormData({ ...formData, tenant_id: value })}
                                    >
                                        <SelectTrigger data-testid="lease-tenant-select">
                                            <SelectValue placeholder="Select tenant" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {tenants.map((tenant) => (
                                                <SelectItem key={tenant.id} value={tenant.id}>
                                                    {tenant.name} ({tenant.email})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Start Date</Label>
                                <Input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                    required
                                    data-testid="lease-start-date"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>End Date</Label>
                                <Input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    required
                                    data-testid="lease-end-date"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Monthly Rent ($)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formData.monthly_rent}
                                    onChange={(e) => setFormData({ ...formData, monthly_rent: e.target.value })}
                                    required
                                    data-testid="lease-monthly-rent"
                                />
                            </div>
                            {!editingLease && (
                                <div className="space-y-2">
                                    <Label>Security Deposit ($)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.security_deposit}
                                        onChange={(e) => setFormData({ ...formData, security_deposit: e.target.value })}
                                        required
                                        data-testid="lease-security-deposit"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Lease Document Upload */}
                        <div className="space-y-2">
                            <Label>Lease Document (PDF)</Label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleDocumentUpload}
                                accept=".pdf,application/pdf"
                                className="hidden"
                            />
                            {leaseDocument ? (
                                <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                                    <div className="flex items-center gap-3">
                                        <FileText className="w-5 h-5 text-blue-600" />
                                        <span className="text-sm text-slate-600">Lease document uploaded</span>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setLeaseDocument(null)}
                                        className="text-red-500 hover:text-red-600"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full border-dashed border-2 h-16 flex items-center justify-center gap-2"
                                    data-testid="upload-lease-doc-button"
                                >
                                    <Upload className="w-4 h-4" />
                                    <span>Upload Lease Document</span>
                                </Button>
                            )}
                            <p className="text-xs text-slate-500">Upload the signed lease agreement (PDF, max 10MB)</p>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-lease-button">
                                {editingLease ? 'Update' : 'Create'} Lease
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
